import { from, Subject } from 'rxjs';
import { concatMap, map, switchMap, toArray } from 'rxjs/operators';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    DocumentTypesEnum,
    IdentityDocumentStatusesEnum,
    AuthApiService,
    IndividualIdentityDetailsService,
    CompanyDocumentModel,
    CompanyExtendedModel,
    IndividualDocumentModel,
    IndividualExtendedModel,
    IndividualIdentityDetailWithFileInputModel,
    IndividualIdentityDetailWithFileInterface,
    FileInputModel,
    IndividualIdentityDetailModel,
    IndividualIdentityDetailInterface,
    IndividualIdentityDetailWithFileModel,
    CompanyDocumentsAtomicService,
    IndividualDocumentsAtomicService
} from '@spa/api';

import { MaxLengthValidatorKey, RequiredValidatorKey } from '@spa/validators';

import { BaseControl, Helper } from '../../../../shared/classes';

import { OtherDocumentsForm } from './other-documents.form';
import { ProofOfAddressForm } from './proof-of-address.form';
import { IdentityDetailsForm } from './identity-details.form';
import { ProofOfIdentityForm } from './proof-of-identity.form';
import { CompanyDocumentsForm, CompanyProofOfAddressForm } from './company';

import { AddDocumentTrait } from '../../traits';
import { AddDocumentFormErrorStatesEnum } from '../../enums';

import {
    IdentityDetailControls,
    AddDocumentMaxNoteLength,
    IdentityDocumentPoISelector,
    IdentityDocumentTypeSelector,
    IdentityDocumentStatusSelector,
    IdentityDocumentNumberSelector,
    ProofOfAddressDocumentTypeSelector,
    IdentityDocumentExpirationDateSelector,
    CompanyProofOfAddressDocumentTypeSelector,
} from '../../../../../assets/descriptors';

export class AddDocumentForm {
    private readonly identityDetailsService: IndividualIdentityDetailsService;
    private readonly isCorporateClient: boolean;
    private readonly companyDocumentsAtomicService?: CompanyDocumentsAtomicService;
    private readonly individualDocumentsAtomicService?: IndividualDocumentsAtomicService;

    public controls: BaseControl<string | number | boolean>[];
    public documentsTuple: [Subject<string>, Subject<string>];
    public documentsTupleErrors: [Subject<string[]>, Subject<string[]>];
    public documentsTupleFiles: [File, File];

    public identityDetailsForm: IdentityDetailsForm;
    public proofOfIdentityForm: ProofOfIdentityForm;
    public proofOfAddressForm: ProofOfAddressForm;
    public otherDocumentsForm: OtherDocumentsForm;

    public companyDocumentsForm: CompanyDocumentsForm;
    public companyProofOfAddressForm: CompanyProofOfAddressForm;

    constructor(
        client: IndividualExtendedModel | CompanyExtendedModel,
        authenticationService: AuthApiService,
        identityDetailsService: IndividualIdentityDetailsService,
        isCorporateClient: boolean,
        companyDocumentsAtomicService: CompanyDocumentsAtomicService,
        individualDocumentsAtomicService: IndividualDocumentsAtomicService
    ) {
        this.documentsTuple = [new Subject(), new Subject()];
        this.documentsTupleErrors = [new Subject(), new Subject()];
        this.documentsTupleFiles = [null, null];
        this.isCorporateClient = isCorporateClient;
        this.identityDetailsService = identityDetailsService;
        this.companyDocumentsAtomicService = companyDocumentsAtomicService;
        this.individualDocumentsAtomicService = individualDocumentsAtomicService;

        this.otherDocumentsForm = new OtherDocumentsForm(
            this.isCorporateClient,
            client as IndividualExtendedModel | CompanyExtendedModel,
            authenticationService,
            companyDocumentsAtomicService,
            individualDocumentsAtomicService
        );

        if (!this.isCorporateClient) {
            this.initIndividualDocumentForms(
                client as IndividualExtendedModel,
                identityDetailsService,
                authenticationService,
                individualDocumentsAtomicService
            );
        } else {
            this.initCompanyDocumentForms(
                client as CompanyExtendedModel,
                authenticationService,
                companyDocumentsAtomicService
            );
        }
    }

    private initIndividualDocumentForms(
        client: IndividualExtendedModel,
        identityDetailsService,
        authenticationService,
        individualDocumentsAtomicService: IndividualDocumentsAtomicService
    ): void {
        const identityDocumentTypes = Helper.enumsHelper().getProofOfIdentityDocumentTypes();

        this.identityDetailsForm = new IdentityDetailsForm(
            client,
            identityDocumentTypes,
            identityDetailsService
        );

        this.proofOfIdentityForm = new ProofOfIdentityForm(
            individualDocumentsAtomicService,
            authenticationService,
            identityDocumentTypes
        );

        const proofOfAddressDocumentTypes = Helper.enumsHelper().getProofOfAddressDocumentTypes();

        this.proofOfAddressForm = new ProofOfAddressForm(proofOfAddressDocumentTypes,
            authenticationService,
            individualDocumentsAtomicService
        );

        this.proofOfIdentityForm.individual = this.proofOfAddressForm.individual = client as IndividualExtendedModel;
    }

    private initCompanyDocumentForms(
        client: CompanyExtendedModel,
        authenticationService,
        companyDocumentsAtomicService
    ): void {
        this.companyDocumentsForm = new CompanyDocumentsForm(
            client as CompanyExtendedModel,
            authenticationService,
            companyDocumentsAtomicService
        );

        this.companyProofOfAddressForm = new CompanyProofOfAddressForm(
            client as CompanyExtendedModel,
            authenticationService,
            companyDocumentsAtomicService
        );
    }

    private initSubForms(): void {
        if (!this.isCorporateClient) {
            this.initIndividualFormsControls();
        } else {
            this.companyDocumentsForm.initFormControl(this.controls[0].value, this.controls[0].placeholder);
            this.companyProofOfAddressForm.initFormControl(this.controls[1].value, this.controls[1].placeholder);

            this.companyProofOfAddressForm.documentFile =
                this.companyDocumentsForm.documentFile =
                this.otherDocumentsForm.documentFile =
                this.documentsTupleFiles[0];

            this.companyProofOfAddressForm.documentErrorsSubject =
                this.companyDocumentsForm.documentErrorsSubject =
                this.otherDocumentsForm.documentErrorsSubject =
                this.documentsTupleErrors[0];

            this.companyProofOfAddressForm.reviewSummary =
                this.companyDocumentsForm.reviewSummary =
                this.otherDocumentsForm.reviewSummary =
                this.controls[4].value as string;

            this.companyProofOfAddressForm.hiddenDocument =
                this.companyDocumentsForm.hiddenDocument =
                this.otherDocumentsForm.hiddenDocument =
                !this.controls[6].value as boolean;
        }
    }

    private initIndividualFormsControls(): void {
        const identityDetailControls = IdentityDetailControls().map((control: BaseControl) => {
            switch (control.name) {
                case IdentityDocumentTypeSelector:
                    control.value = this.controls[1].value as string;
                    control.placeholder = this.controls[1].placeholder;
                    break;
                case IdentityDocumentNumberSelector:
                    control.value = this.controls[2].value as string;
                    control.placeholder = this.controls[2].placeholder;
                    break;
                case IdentityDocumentExpirationDateSelector:
                    control.value = this.controls[3].value as string;
                    control.disabled = this.controls[3].disabled;
                    control.placeholder = this.controls[3].placeholder;
                    break;
                case IdentityDocumentStatusSelector:
                    control.value = IdentityDocumentStatusesEnum.Pending;
            }

            return control;
        });

        this.proofOfIdentityForm.initFormControl(this.controls[1].value);
        this.identityDetailsForm.initForm(false, true, identityDetailControls);
        this.proofOfAddressForm.initFormControl(this.controls[1].value, this.controls[1].placeholder);

        this.proofOfIdentityForm.documentsTupleFiles = this.documentsTupleFiles;
        this.proofOfIdentityForm.documentsTupleErrors = this.documentsTupleErrors;

        this.proofOfAddressForm.documentFile =
            this.otherDocumentsForm.documentFile =
            this.documentsTupleFiles[0];

        this.proofOfAddressForm.documentErrorsSubject =
            this.otherDocumentsForm.documentErrorsSubject =
            this.documentsTupleErrors[0];

        this.proofOfIdentityForm.reviewSummary =
            this.proofOfAddressForm.reviewSummary =
            this.otherDocumentsForm.reviewSummary =
            this.controls[4].value as string;

        this.proofOfIdentityForm.hiddenDocument =
            this.proofOfAddressForm.hiddenDocument =
            this.otherDocumentsForm.hiddenDocument = !this.controls[6].value as boolean;
    }

    private setFormErrors(errors: { [key: string]: string[] }): void {
        Object.keys(errors).map((key: string) => {
            switch (key) {
                case IdentityDocumentTypeSelector:
                case ProofOfAddressDocumentTypeSelector:
                case CompanyProofOfAddressDocumentTypeSelector:
                    this.controls[1].errors = errors[key];
                    break;
                case IdentityDocumentNumberSelector:
                    this.controls[2].errors = errors[key];
                    break;
                case IdentityDocumentExpirationDateSelector:
                    this.controls[3].errors = errors[key];
                    break;
            }
        });
    }

    private validateNonCorporateForms(
        typeControl: BaseControl<string | number | boolean>,
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean {
        switch (typeControl.value) {
            case DocumentTypesEnum.ProofOfIdentity:
                const identityDetailsCheck = this.identityDetailsForm.validateForm(messageFunction);
                const identityDetailsValidationErrors = this.identityDetailsForm.getValidationErrors();
                const proofOfIdentityCheck = this.proofOfIdentityForm.validateDocumentsTuple(messageFunction);

                this.setFormErrors(identityDetailsValidationErrors[0]);

                return identityDetailsCheck && proofOfIdentityCheck;
            case DocumentTypesEnum.ProofOfAddress:
                const proofOfAddressCheck = this.proofOfAddressForm.validateForm(messageFunction);
                const proofOfAddressValidationErrors = this.proofOfAddressForm.getValidationErrors();

                this.setFormErrors(proofOfAddressValidationErrors);

                return proofOfAddressCheck;
            case DocumentTypesEnum.Other:
            case DocumentTypesEnum.Selfie:
                return this.otherDocumentsForm.validateDocumentFile(messageFunction);
        }
    }

    private getNonCorporateDocumentRequest(
        typeControlValue: string | number | boolean
    ): ServerResponse<IndividualDocumentModel | CompanyDocumentModel> {
        switch (typeControlValue) {
            case DocumentTypesEnum.ProofOfIdentity:
                return this.createPoIRegistration();
            case DocumentTypesEnum.ProofOfAddress:
                return this.proofOfAddressForm.createRequest()
                    .pipe(
                        map((proofOfAddressResponse: ServerResponseData<IndividualDocumentModel>) => {
                            if (proofOfAddressResponse.status !== HttpStatusCodesEnum.Ok) {
                                return Helper.errorsHelper().createErrorResponse<IndividualDocumentModel>(
                                    proofOfAddressResponse,
                                    AddDocumentFormErrorStatesEnum.CreatePoAFailed
                                );
                            }

                            const individualDocument = proofOfAddressResponse.data as IndividualDocumentModel;

                            return Helper.createSuccessResponse<IndividualDocumentModel>(individualDocument);
                        })
                    );
            case DocumentTypesEnum.Other:
            case DocumentTypesEnum.Selfie:
                return this.otherDocumentsForm.createRequest(typeControlValue as string)
                    .pipe(
                        map((proofOfAddressResponse: ServerResponseData<IndividualDocumentModel>) => {
                            if (proofOfAddressResponse.status !== HttpStatusCodesEnum.Ok) {
                                const errorState = typeControlValue !== DocumentTypesEnum.Selfie ?
                                    AddDocumentFormErrorStatesEnum.CreateOtherFailed :
                                    AddDocumentFormErrorStatesEnum.CreateSelfieFailed;

                                return Helper.errorsHelper().createErrorResponse<IndividualDocumentModel>(
                                    proofOfAddressResponse,
                                    errorState
                                );
                            }

                            const individualDocument = proofOfAddressResponse.data as IndividualDocumentModel;

                            return Helper.createSuccessResponse<IndividualDocumentModel>(individualDocument);
                        })
                    );
        }
    }

    private createPoIRegistration(): ServerResponse<IndividualDocumentModel> {
        const obj = this.proofOfIdentityForm.getRequestModel();
        const identityModel = this.identityDetailsForm.getRequestModel();

        return from(obj[0]).pipe(
            concatMap((x: [File, number]) => {
                return Helper.filesHelper().convertFileToRawBase64(x[0])
                    .pipe(
                        map((y: string) => {
                            if (y == null) {
                                return null;
                            }

                            return new FileInputModel(x[0].name, y);
                        })
                    );
            }),
            toArray(),
            switchMap((files) => {
                if (files.filter(x => x == null).length > 0) {
                    return Helper.errorsHelper().createBadRequestResponseObservable(
                        AddDocumentFormErrorStatesEnum.CreateIdentityDetailFailed
                    );
                }
                const model = new IndividualIdentityDetailWithFileInputModel(
                    identityModel.identityDocumentType,
                    identityModel.identityNumber,
                    identityModel.expirationDate,
                    identityModel.individualId,
                    identityModel.reviewStatus,
                    identityModel.reviewSummary,
                    files,
                    this.proofOfIdentityForm.hiddenDocument
                );

                return this.identityDetailsService.createWithFile(model).pipe(
                    map((identityDetailsResponse: ServerResponseData<IndividualIdentityDetailWithFileInterface>) => {
                        if (identityDetailsResponse.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<IndividualIdentityDetailWithFileInterface>(
                                identityDetailsResponse,
                                AddDocumentFormErrorStatesEnum.CreateIdentityDetailFailed
                            );
                        }
                        const individualDocument = new IndividualIdentityDetailWithFileModel(
                            identityDetailsResponse.data as IndividualIdentityDetailWithFileInterface).individualDocument;
                        const identityDetailsStatus = !this.controls[5]
                            .value
                            ? IdentityDocumentStatusesEnum.Pending
                            : IdentityDocumentStatusesEnum.Approved;

                        const identityFormValues = {
                            [IdentityDocumentPoISelector]: individualDocument.id,
                            [IdentityDocumentStatusSelector]: identityDetailsStatus,
                        };

                        this.identityDetailsForm.setValues(0, identityFormValues);
                        const identityDetailModel = new IndividualIdentityDetailModel(
                            identityDetailsResponse.data as IndividualIdentityDetailInterface);
                        if (!this.identityDetailsForm.individual.identityDetails) {
                            this.identityDetailsForm.individual.identityDetails = [];
                        }
                        this.identityDetailsForm.individual.identityDetails.push(identityDetailModel);

                        if (!this.proofOfIdentityForm.individual.documents) {
                            this.proofOfIdentityForm.individual.documents = [];
                        }

                        this.proofOfIdentityForm.individual.documents.push(individualDocument);
                        this.proofOfIdentityForm.initDocumentsTuple(this.proofOfIdentityForm.individual.documents);

                        return Helper.createSuccessResponse<IndividualDocumentModel>(
                            individualDocument
                        );
                    })
                );
            })
        );
    }

    onDocumentUpload(file: File, index: number): void {
        this.documentsTupleFiles[index] = file;
    }

    resetDocumentsTuplesItem(index: number): void {
        this.documentsTuple[index].next(null);
        this.documentsTupleFiles[index] = null;
        this.documentsTupleErrors[index].next([]);

        this.otherDocumentsForm.resetDocument();

        if (!this.isCorporateClient) {
            this.proofOfIdentityForm.resetDocumentsTuplesItem(index);
            this.proofOfAddressForm.resetDocument();
        } else {
            this.companyDocumentsForm.resetDocument();
            this.companyProofOfAddressForm.resetDocument();
        }
    }

    isSetAsActiveChecked(): boolean {
        return this.controls[5].value as boolean;
    }

    initForm(selfInit = true, documentType = null): void {
        if (!selfInit) {
            this.initSubForms();
        } else {
            this.controls = AddDocumentTrait.initControls(documentType, this.isCorporateClient);
        }
    }

    validateForm(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }): boolean {
        let typesCheck = true;
        let noteCheck = true;

        const typeControl = this.controls[0];
        const noteControl = this.controls[4];

        if (!typeControl.value) {
            typesCheck = false;
            typeControl.setErrors([messageFunction(typeControl.placeholder)[RequiredValidatorKey]]);
        }

        if (noteControl.value && ((noteControl.value as string).length > AddDocumentMaxNoteLength)) {
            noteCheck = false;
            noteControl.setErrors([messageFunction(noteControl.placeholder, AddDocumentMaxNoteLength)[MaxLengthValidatorKey]]);
        }

        const typeAndNoteCheck = typesCheck && noteCheck;

        if (!this.isCorporateClient) {
            return typeAndNoteCheck && this.validateNonCorporateForms(typeControl, messageFunction);
        } else {
            switch (typeControl.value) {
                case DocumentTypesEnum.ProofOfAddress:
                    const companyPoACheck = this.companyProofOfAddressForm.validateForm(messageFunction);
                    const companyPoAValidationErrors = this.companyProofOfAddressForm.getValidationErrors();

                    this.setFormErrors(companyPoAValidationErrors);

                    return typeAndNoteCheck && companyPoACheck;
                case DocumentTypesEnum.Other:
                    return typeAndNoteCheck && this.otherDocumentsForm.validateDocumentFile(messageFunction);
                default:
                    return typeAndNoteCheck && this.companyDocumentsForm.validateForm(messageFunction);
            }
        }
    }

    createRequest(): ServerResponse<IndividualDocumentModel | CompanyDocumentModel> | null {
        let request: ServerResponse<IndividualDocumentModel | CompanyDocumentModel> = null;

        const typeControl = this.controls[0];

        if (!this.isCorporateClient) {
            request = this.getNonCorporateDocumentRequest(typeControl.value);
        } else {

            let documentRequest = null;

            switch (typeControl.value) {
                case DocumentTypesEnum.ProofOfAddress:
                    documentRequest = this.companyProofOfAddressForm.createRequest(false);
                    break;
                case DocumentTypesEnum.Other:
                    documentRequest = this.otherDocumentsForm.createRequest(typeControl.value as string);
                    break;
                default:
                    documentRequest = this.companyDocumentsForm.createRequest(false);
                    break;
            }

            request = documentRequest.pipe(
                map((response: ServerResponseData<CompanyDocumentModel>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        let errorState = null;

                        switch (typeControl.value) {
                            case DocumentTypesEnum.ProofOfAddress:
                                errorState = AddDocumentFormErrorStatesEnum.CreateCompanyPoAFailed;
                                break;
                            case DocumentTypesEnum.Other:
                                errorState = AddDocumentFormErrorStatesEnum.CreateCompanyOtherDocumentFailed;
                                break;
                            default:
                                errorState = AddDocumentFormErrorStatesEnum.CreateCompanyDocumentFailed;
                                break;
                        }

                        return Helper.errorsHelper().createErrorResponse(response, errorState);
                    }

                    return Helper.createSuccessResponse<CompanyDocumentModel>(response.data as CompanyDocumentModel);
                })
            );
        }

        return request;
    }
}
